import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { grey400 } from 'ext-ui/style/colors'
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import GenericLayoutDetailsComponent from 'src/components/GenericLayoutDetailsComponent/GenericLayoutDetailsComponent'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import { FormFieldsValidation, useFormFields } from 'src/hooks'
import Trns, { translateSingleString } from 'src/localization/Trns'
import {
  UpdateDepartmentBody,
  usePutUpdateDepartment,
  useSuspenseQueryDepartmentDetails,
} from 'src/queries/organizations/department.queries'
import {
  selectNullSafeLocaleFromState,
  selectUserListModelFromState,
} from 'src/redux/selectors/auth.selectors'
import {
  selectDepartmentIdFromRoute,
  selectOrganizationIdFromRoute,
} from 'src/redux/selectors/data/organizations.selectors'
import {
  stringLengthIsBetween,
  stringLengthIsLessThan,
} from 'src/utils/validation/string.validation'
import styled, { css } from 'styled-components'

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
`

const Size20Span = styled.span`
  ${getFontCss(
    20,
    themeConstants.colors.font_primary,
    styleConstants.fontFamilies.medium,
  )}
`
const Size14Span = styled.span`
  ${getFontCss(
    14,
    styleConstants.colors.gray_darkest,
    styleConstants.fontFamilies.regular,
  )}
`
const Size16Span = styled.span`
  ${getFontCss(
    16,
    themeConstants.colors.font_primary,
    styleConstants.fontFamilies.regular,
  )}
  text-overflow: ellipsis;
`
export const DepartmentDetailsFields = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding-top: 20px;
  align-self: stretch;
  &:not(:last-child) {
    margin-bottom: 20px;
  }
  &:nth-child(n + 2) {
    border-top: 1px solid ${grey400};
  }
  overflow: hidden;
`
const InputSpan = styled.span`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`

const DepartmentDetailsComponent = (): React.ReactElement => {
  const organizationId = useSelector(selectOrganizationIdFromRoute)
  const departmentId = useSelector(selectDepartmentIdFromRoute)
  const locale = useSelector(selectNullSafeLocaleFromState)
  const user = useSelector(selectUserListModelFromState)
  const { analytics } = useAnalyticsContext()

  const {
    mutateAsync: putUpdateDepartment,
    isPending: putUpdateDepartmentLoading,
    error: putUpdateDepartmentError,
  } = usePutUpdateDepartment(organizationId, departmentId)
  const { data: departmentDetails, status: departmentDetailsStatus } =
    useSuspenseQueryDepartmentDetails({ organizationId, departmentId })

  const takeStringIfNotEmpty = (val?: string): string | undefined =>
    val !== undefined && (val?.trim()?.length || 0) > 0
      ? (val ?? undefined)
      : undefined
  const {
    fields,
    fieldsErrors,
    onFieldChange,
    setFieldsErrors,
    setErrorsAndReturnDidPassValidation,
    setFields,
  } = useFormFields<UpdateDepartmentBody>(
    {
      name: departmentDetails?.name || '',
      description: departmentDetails?.description || '',
      code: departmentDetails?.code || '',
    },
    (): FormFieldsValidation<UpdateDepartmentBody> => ({
      name: [stringLengthIsBetween(1, 50)],
      description: [stringLengthIsLessThan(200)],
      code: [stringLengthIsLessThan(20)],
    }),
  )
  useEffect(() => {
    if (
      departmentDetailsStatus === 'success' &&
      departmentDetails !== undefined
    ) {
      setFields({
        name: departmentDetails.name || '',
        description: departmentDetails.description || '',
        code: departmentDetails.code || '',
      })
    }
  }, [departmentDetailsStatus, departmentDetails])

  const onSubmit = async (e?: React.FormEvent): Promise<boolean> => {
    e?.preventDefault()
    if (setErrorsAndReturnDidPassValidation()) {
      await putUpdateDepartment({
        organizationId,
        departmentId: departmentDetails?.id || departmentId,
        body: {
          name: fields.name,
          description: takeStringIfNotEmpty(fields.description),
          code: takeStringIfNotEmpty(fields.code),
        },
      })
    }
    analytics.track('department details - save changes', {
      department_name: fields.name !== departmentDetails?.name,
      department_description:
        fields.description !== departmentDetails?.description,
      department_code: fields.code !== departmentDetails?.code,
    })
    return !!setErrorsAndReturnDidPassValidation()
  }

  const showStrOrDash = (field: string | undefined): string => {
    if (field === undefined || field === '') {
      return '--'
    } else return field ?? '--'
  }

  const viewDepartmentDetails = (
    <FlexColumn
      css={css`
        gap: 20px;
      `}
    >
      <FlexColumn
        css={css`
          gap: 8px;
        `}
      >
        <Size14Span>
          <Trns source={'Team Name'} />
        </Size14Span>
        <Size20Span>{showStrOrDash(departmentDetails?.name)}</Size20Span>
      </FlexColumn>
      <FlexColumn>
        <DepartmentDetailsFields>
          <Size14Span>
            <Trns source={'Team Description'} />
          </Size14Span>
          <Size16Span>
            <span
              css={css`
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              `}
            >
              {showStrOrDash(departmentDetails?.description)}
            </span>
          </Size16Span>
        </DepartmentDetailsFields>
        <DepartmentDetailsFields>
          <Size14Span>
            <Trns source={'Team Code'} />
          </Size14Span>
          <Size16Span>{showStrOrDash(departmentDetails?.code)}</Size16Span>
        </DepartmentDetailsFields>
      </FlexColumn>
    </FlexColumn>
  )

  const updateDepartmentDetails = user?.hasOrgAdminPermissions ? (
    <FlexColumn
      css={css`
        gap: 20px;
      `}
    >
      <InputSpan>
        <TextInputField
          design={TextInputFieldDesigns.New}
          placeholder={translateSingleString(locale, 'Enter a team name')}
          fieldLabel={
            <span
              css={css`
                ${getFontCss(
                  14,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )}
              `}
            >
              <Trns source={'Team Name'} />
            </span>
          }
          value={fields.name}
          characterLimit={50}
          onChange={(value: string): void => onFieldChange('name', value)}
          errors={fieldsErrors.name}
          testId={'name'}
        />
      </InputSpan>
      <InputSpan>
        <TextInputField
          design={TextInputFieldDesigns.New}
          placeholder={translateSingleString(
            locale,
            'Enter a team description',
          )}
          fieldLabel={
            <span
              css={css`
                ${getFontCss(
                  14,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )}
              `}
            >
              <Trns source={'Team Description'} />
            </span>
          }
          value={fields.description}
          characterLimit={200}
          hideCharacterLimitCount
          isOptional
          onChange={(value: string): void =>
            onFieldChange('description', value)
          }
          errors={fieldsErrors.description}
          testId={'description'}
        />
      </InputSpan>
      <InputSpan>
        <TextInputField
          design={TextInputFieldDesigns.New}
          placeholder={translateSingleString(locale, 'Enter a team code')}
          fieldLabel={
            <span
              css={css`
                ${getFontCss(
                  14,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )}
              `}
            >
              <Trns source={'Team Code'} />
            </span>
          }
          value={fields.code}
          characterLimit={20}
          isOptional
          onChange={(value: string): void => onFieldChange('code', value)}
          errors={fieldsErrors.code}
          testId={'code'}
        />
      </InputSpan>
    </FlexColumn>
  ) : undefined

  return (
    <>
      <GenericLayoutDetailsComponent
        staticContent={viewDepartmentDetails}
        updatingContent={updateDepartmentDetails}
        onSave={onSubmit}
        onCancel={(): void => {
          onFieldChange('name', departmentDetails?.name || '')
          onFieldChange('description', departmentDetails?.description)
          onFieldChange('code', departmentDetails?.code)
          setFieldsErrors({
            name: undefined,
            description: undefined,
            code: undefined,
          })
          analytics.track('department details - cancel')
        }}
        saveLoading={putUpdateDepartmentLoading}
        error={putUpdateDepartmentError?.error}
      />
    </>
  )
}

export default DepartmentDetailsComponent
